import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, ShieldCheck, KeyRound } from "lucide-react";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("Admin");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(username, password);
      nav("/admin");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-gradient-to-br from-blue-950 via-blue-900 to-blue-700 text-white">
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 60% 70%, white 1px, transparent 1px)",
            backgroundSize: "40px 40px, 60px 60px",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-white/10 backdrop-blur grid place-items-center">
              <GraduationCap />
            </div>
            <span className="font-display text-xl font-bold">Lovely Institute</span>
          </div>
          <div>
            <h1 className="font-display text-5xl font-bold leading-tight mb-4">
              Every graduate<br />
              deserves a certificate<br />
              worth framing.
            </h1>
            <p className="text-blue-100/80 text-lg max-w-md">
              Design, generate and print professional student certificates in
              under a minute — all from one clean workspace.
            </p>
          </div>
          <div className="flex gap-6 text-sm text-blue-100/80">
            <span className="flex items-center gap-2"><ShieldCheck size={16}/> Secure admin access</span>
            <span className="flex items-center gap-2"><KeyRound size={16}/> Local backups</span>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="mb-8">
            <div className="lg:hidden flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-800 grid place-items-center text-white">
                <GraduationCap size={22} />
              </div>
              <span className="font-display text-lg font-bold">Lovely Institute</span>
            </div>
            <h2 className="font-display text-3xl font-bold text-slate-900">
              Welcome back
            </h2>
            <p className="text-slate-500 mt-1">
              Sign in as administrator to continue.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1.5 h-11"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>
            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              >
                {err}
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-11 bg-blue-800 hover:bg-blue-900 transition-soft font-medium"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>

          <p className="text-xs text-slate-400 mt-6 text-center">
            Default credentials: <b>Admin</b> / <b>admin123</b> — change in Settings.
          </p>
        </form>
      </div>
    </div>
  );
}
